import { Base } from '@studiometa/js-toolkit';
import Accordion from '../Accordion/index.js';
import { debounce, historyReplace, trapFocus, untrapFocus } from '@studiometa/js-toolkit/utils';

const QUERY_DEBOUNCE = 250;

/** Below `m`, where the facets are a panel rather than the left column. */
const PANEL_BREAKPOINTS = new Set(['xxs', 'xs', 's']);
/**
 * The contract's serving ceiling: matches are counted past it, but no page holds one, so paging
 * towards them would answer empty.
 */
const MAX_TOTAL_HITS = 1000;
const MAX_SEARCH_LIMIT = 50;

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'svg', 'gif', 'webp', 'avif']);

/**
 * Combien d'étiquettes une carte porte au plus. Le brief en déclare quatre familles (§2.4) et
 * chacune peut valoir plusieurs fois : les laisser toutes recouvrirait la vignette qu'elles
 * annotent.
 */
const MAX_BADGES = 3;

/**
 * Le corps d'une réponse en erreur, tronqué, ou de quoi dire qu'il n'y en avait pas.
 * @param {Response} response The refused response.
 * @returns {Promise<string>}
 */
async function body(response) {
  try {
    return (await response.text()).slice(0, 500) || 'corps vide';
  } catch {
    return 'corps illisible';
  }
}

/**
 * Documentation search.
 *
 * Reads `GET /apps/search` (the "ikko search" app proxy) and renders the answer from the
 * `<template>` elements the section provides.
 */
export default class DocumentationSearch extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'DocumentationSearch',
    components: {
      Accordion,
    },
    refs: [
      'form',
      'entries',
      'resultsPanel',
      'loading',
      'query',
      'page',
      'chips',
      'summary',
      'selected',
      'results',
      'empty',
      'error',
      'loadMore',
      'loaded',
      'progress',
      'more',
      'moreSpinner',
      'actions',
      'selectAll',
      'selectAllSpinner',
      'clearSelection',
      'downloadSelection',
      'downloadSpinner',
      'filtersPanel',
      'filtersBar',
      'filtersOpen',
      'filtersClose',
      'filtersClear[]',
      'filtersApply',
      'hitTemplate',
      'chipTemplate',
      'badgeTemplate',
      'facetGroup[]',
      'facetValue[]',
      'facetInput[]',
      'facetCount[]',
    ],
    options: {
      endpoint: {
        type: String,
        default: '/apps/search',
      },
      badgeKeys: {
        type: String,
        default: '',
      },
      langKey: {
        type: String,
        default: '',
      },
      langFallback: {
        type: String,
        default: '',
      },
    },
  };

  /**
   * The request in flight.
   * @type {AbortController|null}
   */
  controller = null;

  /**
   * The documents the visitor has picked, keyed by hit id, each carrying what the download needs.
   * @type {Map<string, { url: string, name: string, hit: object }>}
   */
  selection = new Map();

  /**
   * The hits currently rendered, by id
   * @type {Map<string, object>}
   */
  rendered = new Map();

  /**
   * Whether the list shows the selection instead of the search results.
   * @type {boolean}
   */
  showingSelection = false;

  /**
   * Combien de documents la recherche courante ramène, écran compris ou non.
   * @type {number}
   */
  totalHits = 0;

  /**
   * Whether the facets panel is open. Only meaningful below `m`, where the facets are a panel.
   * @type {boolean}
   */
  filtersAreOpen = false;

  /**
   * Les éléments que l'ouverture du panneau a rendus inertes, et eux seuls.
   * @type {HTMLElement[]|null}
   */
  backgroundInert = null;

  /**
   * Les familles affichées en étiquette sur la carte plutôt qu'en filtre — la colonne « Étiquette
   * sur card » du brief. Leurs valeurs arrivent sur chaque hit sans être demandées en `facets[]` :
   * l'app indexe `meta.<clé>` dès que la facette est activée dans ses réglages, et `facets[]` ne
   * gouverne que les distributions. Une étiquette ne coûte donc aucun compteur.
   * @returns {string[]}
   */
  get badgeKeys() {
    return this.$options.badgeKeys.split(',').filter(Boolean);
  }

  /**
   * Whether the facets are presented as a panel rather than as the left column.
   * @returns {boolean}
   */
  get facetsArePanel() {
    return PANEL_BREAKPOINTS.has(this.$services.get('resized').breakpoint);
  }

  /**
   * Fetch on mount so the page reads as a browsable listing, and restore whatever the URL
   * carried.
   *
   * Whether the address bar asks for a search rather than the entry grid.
   * @returns {boolean}
   */
  get hasActiveSearch() {
    const params = new URLSearchParams(window.location.search);

    if (params.get('q')) {
      return true;
    }

    return [...params.keys()].some((key) => key.startsWith('m['));
  }

  /**
   *
   */
  mounted() {
    this.onSelectChange = (event) => {
      const input = event.target.closest('[data-hit="select"]');

      if (!input) {
        return;
      }

      if (input.checked) {
        this.selection.set(input.value, {
          url: input.dataset.file,
          name: input.dataset.name,
          hit: this.rendered.get(input.value),
        });
      } else {
        this.selection.delete(input.value);
      }

      if (this.showingSelection) {
        this.renderSelectionView();
      } else {
        this.renderSelection();
      }
    };
    this.$refs.results.addEventListener('change', this.onSelectChange);

    this.onCardClick = (event) => {
      const card = event.target.closest('[data-hit="card"]');

      if (!card || card.getAttribute('aria-disabled') === 'true') {
        return;
      }

      if (event.target.closest('label, [data-hit="download"]')) {
        return;
      }

      const input = card.querySelector('[data-hit="select"]');

      if (!input) {
        return;
      }

      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    this.$refs.results.addEventListener('click', this.onCardClick);
    this.panelObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ target }) => {
        target.inert = target.getAttribute('aria-hidden') === 'true';
      });
    });
    this.$el.querySelectorAll('[data-ref="content"][aria-hidden]').forEach((panel) => {
      this.panelObserver.observe(panel, { attributeFilter: ['aria-hidden'] });
    });

    this.debouncedSearch = debounce(() => this.search(), QUERY_DEBOUNCE);

    this.restoreFromUrl();
    if (!this.$refs.entries || this.hasActiveSearch) {
      this.showLoading();
    }

    this.header = document.querySelector('#main-header');
    // Its slide outlasts the scroll that triggered it, so `scrolled` alone misses the end.
    this.onHeaderTransitionEnd = () => this.syncStickyTop();
    this.header?.addEventListener('transitionend', this.onHeaderTransitionEnd);

    this.syncStickyTop();
    this.syncFilters();

    this.search();
  }

  /**
   * Rest the sticky "Filtrer" button on the header's bottom edge — not its height, which left
   * the button hanging in the gap once the theme slid the header away. Clamped: a header on
   * its way out reports a negative edge.
   * @returns {void}
   */
  syncStickyTop() {
    const edge = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;

    this.$el.style.setProperty('--documentation-sticky-top', `${edge}px`);
  }

  /**
   * The header moves with the scroll, so the resting place is recomputed as it goes.
   * @returns {void}
   */
  scrolled() {
    this.syncStickyTop();
  }

  /**
   * Crossing `m` changes what the facets ARE, so the state is re-stated rather than remembered.
   * @returns {void}
   */
  resized() {
    this.syncStickyTop();
    this.syncFilters();
  }

  /**
   * Swap the entry grid for the loading skeleton.
   * @returns {void}
   */
  showLoading() {
    if (!this.$refs.resultsPanel.classList.contains('hidden')) {
      return;
    }

    this.$refs.entries?.classList.add('hidden');
    this.$refs.loading.classList.remove('hidden');
  }

  /**
   * Hand the column over to the results.
   * @returns {void}
   */
  showResults() {
    this.$refs.entries?.classList.add('hidden');
    this.$refs.loading.classList.add('hidden');
    this.$refs.resultsPanel.classList.remove('hidden');
  }

  /**
   * Drop the request in flight, if any.
   */
  destroyed() {
    this.releaseFiltersScroll();
    this.controller?.abort();
    this.panelObserver?.disconnect();
    this.$refs.results.removeEventListener('change', this.onSelectChange);
    this.$refs.results.removeEventListener('click', this.onCardClick);
    this.header?.removeEventListener('transitionend', this.onHeaderTransitionEnd);
    // Released directly, not via `closeFilters`: its services may already be gone.
    this.filtersAreOpen = false;
    this.releaseFiltersScroll();

    if (this.onFiltersKeyup) {
      document.removeEventListener('keyup', this.onFiltersKeyup);
      untrapFocus();
    }
  }

  /**
   * Restate what the facets are at the current width: a dialog below `m`, a plain column above.
   * `role`, `aria-modal` and `inert` have no breakpoint form, hence the JS.
   * @returns {void}
   */
  syncFilters() {
    const { filtersPanel } = this.$refs;

    if (!filtersPanel) {
      return;
    }

    if (!this.facetsArePanel) {
      this.filtersAreOpen = false;
      this.releaseFiltersScroll();
      filtersPanel.removeAttribute('role');
      filtersPanel.removeAttribute('aria-modal');
      filtersPanel.removeAttribute('aria-labelledby');
      filtersPanel.removeAttribute('aria-hidden');
      filtersPanel.inert = false;
      return;
    }

    filtersPanel.setAttribute('role', 'dialog');
    filtersPanel.setAttribute('aria-modal', 'true');
    filtersPanel.setAttribute('aria-labelledby', 'documentation-filters-title');
    filtersPanel.setAttribute('aria-hidden', String(!this.filtersAreOpen));
    // Off-screen, not removed: the `sr-only` checkboxes would stay in the tab order.
    filtersPanel.inert = !this.filtersAreOpen;
  }

  /**
   * `is-panel-open` is the theme's signal: it clears the header out of the panel's way.
   * @returns {void}
   */
  lockFiltersScroll() {
    document.body.classList.add('overflow-hidden', 'is-panel-open');
    this.setBackgroundInert(true);
  }

  /**
   * @returns {void}
   */
  releaseFiltersScroll() {
    document.body.classList.remove('overflow-hidden', 'is-panel-open');
    this.setBackgroundInert(false);
  }

  /**
   * `aria-modal` annonce que le reste de la page est hors d'atteinte, mais ne le rend pas vrai :
   * le piège de focus retient le clavier, pas le curseur virtuel d'un lecteur d'écran. Chaque
   * frère sur le chemin du panneau est donc neutralisé, et seul ce que l'on a posé est rétabli —
   * le thème pose `inert` ailleurs de son côté.
   * @param {boolean} isInert Whether the page behind the panel is out of reach.
   * @returns {void}
   */
  setBackgroundInert(isInert) {
    if (!isInert) {
      this.backgroundInert?.forEach((element) => {
        element.inert = false;
      });
      this.backgroundInert = null;

      return;
    }

    this.backgroundInert = [];

    for (
      let node = this.$refs.filtersPanel;
      node && node !== document.body;
      node = node.parentElement
    ) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node && sibling instanceof HTMLElement && !sibling.inert) {
          sibling.inert = true;
          this.backgroundInert.push(sibling);
        }
      }
    }
  }

  /**
   * @returns {void}
   */
  onFiltersOpenClick() {
    this.openFilters();
  }

  /**
   * Closes the panel. The facets already relaunch the search, so there is nothing to submit.
   * @returns {void}
   */
  onFiltersApplyClick() {
    this.closeFilters();
  }

  /**
   * @returns {void}
   */
  onFiltersCloseClick() {
    this.closeFilters();
  }

  /**
   * Remet la recherche à zéro : toute facette décochée, langue comprise, et le champ vidé.
   * Deux boutons portent le ref — la colonne en desktop, l'en-tête du panneau en mobile, qui
   * reste ouvert : seule la liste se relance.
   * @returns {void}
   */
  onFiltersClearClick() {
    const checked = this.$refs.facetInput.filter((input) => input.checked);

    if (checked.length === 0 && this.$refs.query.value === '') {
      return;
    }

    checked.forEach((input) => {
      input.checked = false;
    });
    this.$refs.query.value = '';
    this.showLoading();
    this.resetPage();
    this.search();
  }

  /**
   * @param {object} context The keyboard service context.
   * @param {boolean} context.ESC Whether escape was pressed.
   * @returns {void}
   */
  keyed({ ESC }) {
    if (ESC) {
      this.closeFilters();
    }
  }

  /**
   * @returns {void}
   */
  openFilters() {
    if (!this.facetsArePanel || this.filtersAreOpen) {
      return;
    }

    this.filtersAreOpen = true;
    this.syncFilters();
    this.lockFiltersScroll();

    this.onFiltersKeyup ??= (event) => trapFocus(this.$refs.filtersPanel, event);
    document.addEventListener('keyup', this.onFiltersKeyup);

    this.$refs.filtersClose?.focus();
  }

  /**
   * @param {object} [options] Call options.
   * @param {boolean} [options.restoreFocus] Whether focus returns to the button that opened it.
   * @returns {void}
   */
  closeFilters({ restoreFocus = true } = {}) {
    if (!this.filtersAreOpen) {
      return;
    }

    this.filtersAreOpen = false;
    this.syncFilters();
    this.releaseFiltersScroll();

    if (this.onFiltersKeyup) {
      document.removeEventListener('keyup', this.onFiltersKeyup);
    }

    untrapFocus();

    if (restoreFocus) {
      this.$refs.filtersOpen?.focus();
    }
  }

  /**
   * Check the boxes the URL names, so a shared search is restored before the first fetch.
   *
   * Only for a URL this section WROTE — `blog` travels with every one of them. Without it the
   * visitor is arriving (a bare address, an editorial shortcut), and the markup's own checked
   * language stands: read as a search, such a URL would silently un-check it.
   * @returns {void}
   */
  restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    this.$refs.query.value = params.get('q') ?? '';

    if (!params.has('blog')) {
      return;
    }

    this.$refs.facetInput.forEach((input) => {
      input.checked = params.getAll(input.name).includes(input.value);
    });
  }

  /**
   * Submit the search form.
   * @param {SubmitEvent} event The submit event.
   * @returns {void}
   */
  onFormSubmit({ event }) {
    event.preventDefault();
    this.showLoading();
    this.resetPage();
    this.search();
  }

  /**
   * @returns {void}
   */
  onQueryInput() {
    this.showLoading();
    this.resetPage();
    this.renderClear();
    this.debouncedSearch();
  }

  /**
   * On a facet input change, the search is run immediately.
   * @returns {void}
   */
  onFacetInputChange() {
    this.showLoading();
    this.resetPage();
    this.search();
  }

  /**
   * "See more" asks for the NEXT page and appends it.
   * @returns {void}
   */
  async onMoreClick() {
    this.$refs.page.value = String(this.currentPage + 1);
    this.setLoadingMore(true);

    try {
      await this.search({ append: true });
    } finally {
      this.setLoadingMore(false);
    }
  }

  /**
   * The button's busy state
   * @param {boolean} isLoading
   * @returns {void}
   */
  setLoadingMore(isLoading) {
    this.$refs.more.disabled = isLoading;
    this.$refs.more.setAttribute('aria-busy', String(isLoading));
    this.$refs.moreSpinner.classList.toggle('hidden', !isLoading);
  }

  /**
   * The page currently requested.
   * @returns {number}
   */
  get currentPage() {
    return Number.parseInt(this.$refs.page.value, 10) || 1;
  }

  /**
   * An empty value reads as "not sent" for the endpoint.
   * @returns {void}
   */
  resetPage() {
    this.$refs.page.value = '';
  }

  /**
   * The current form state, as the query string both the endpoint and the address bar take.
   * @returns {URLSearchParams}
   */
  get params() {
    const params = new URLSearchParams();

    for (const [name, value] of new FormData(this.$refs.form).entries()) {
      if (typeof value === 'string' && value.trim() !== '') {
        params.append(name, value.trim());
      }
    }

    return params;
  }

  /**
   * One request against the endpoint.
   * @param {URLSearchParams} params The query to send.
   * @returns {Promise<object>}
   */
  async fetchSearch(params) {
    const response = await fetch(`${this.$options.endpoint}?${this.sent(params)}`, {
      signal: this.controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      // Un refus de l'app NOMME ce qui cloche — la clé de facette inconnue, le paramètre invalide,
      // la liste de ce que la boutique indexe. Sans le corps, « answered 400 » ne laisse rien à
      // corriger.
      throw new Error(`The search endpoint answered ${response.status}: ${await body(response)}`);
    }

    return response.json();
  }

  /**
   * La requête telle qu'elle part, la sélection de langue complétée de sa valeur neutre — les
   * documents sans langue renseignée restent dans les résultats d'un filtre de langue.
   *
   * Ajoutée ici plutôt que dans {@link params}, qui est aussi ce que la barre d'adresse reçoit :
   * un repli implicite n'a rien à faire dans une URL partagée, et aucune case ne le porte.
   * @param {URLSearchParams} params The form state.
   * @returns {URLSearchParams}
   */
  sent(params) {
    const { langKey, langFallback } = this.$options;
    const name = `m[${langKey}][]`;

    if (!langKey || !langFallback || params.getAll(name).length === 0) {
      return params;
    }

    const sent = new URLSearchParams(params);
    sent.append(name, langFallback);

    return sent;
  }

  /**
   * Run the search for the current form state.
   * @param {object} [options] Call options.
   * @param {boolean} [options.append] Extend the list instead of replacing it.
   * @returns {Promise<void>}
   */
  async search({ append = false } = {}) {
    const { params } = this;

    this.showingSelection = false;
    this.$refs.summary.classList.remove('hidden');

    this.controller?.abort();
    this.controller = new AbortController();

    const shared = new URLSearchParams(params);
    shared.delete('page');
    historyReplace({ search: shared });

    try {
      this.render(await this.fetchSearch(params), append);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error(error);

      if (!this.$refs.loading.classList.contains('hidden')) {
        this.showResults();
      }

      this.$refs.error.classList.remove('hidden');
    }
  }

  /**
   * @param {object} data The endpoint's answer.
   * @param {boolean} append Whether the hits extend the list instead of replacing it.
   * @returns {void}
   */
  render(data, append) {
    if (!this.$refs.loading.classList.contains('hidden')) {
      this.showResults();
    }

    this.$refs.error.classList.add('hidden');
    this.totalHits = data.totalHits;
    this.renderHits(data, append);
    this.renderCounts(data);
    this.renderChips();
    this.renderClear();
    this.renderSummary(data);
    this.renderSelection();
    this.renderLoadMore(data);
  }

  /**
   * One hit → one resource card, cloned from the Liquid template.
   *
   * Every string the card shows comes from the answer or from Liquid; nothing is written here.
   * @param {object} data The endpoint's answer.
   * @param {boolean} append Whether the hits extend the list instead of replacing it.
   * @returns {void}
   */
  renderHits(data, append) {
    const fragment = document.createDocumentFragment();

    if (!append) {
      this.rendered.clear();
    }

    data.hits.forEach((hit) => {
      const node = this.$refs.hitTemplate.content.cloneNode(true);
      const find = (name) => node.querySelector(`[data-hit="${name}"]`);

      this.rendered.set(hit.id, hit);

      find('badges').replaceChildren(...this.badgesFor(hit));

      const extension = hit.fields?.['custom.extension'] ?? '';
      find('format').textContent = extension;

      find('title').textContent = hit.title;
      const select = find('select');
      select.value = hit.id;
      select.checked = this.selection.has(hit.id);
      const entry = DocumentationSearch.fileOf(hit);

      select.disabled = !entry;
      select.dataset.file = entry?.url ?? '';
      select.dataset.name = entry?.name ?? '';
      find('card').setAttribute('aria-disabled', String(!entry));

      const file = hit.fields?.['custom.fichier'];
      // The connector renders a thumbnail for PDFs and for images too heavy to show as is.
      const thumbnail = hit.fields?.['custom.thumbnail'];

      const mark = find('format-mark');
      const preview = find('preview');
      const previewSource =
        thumbnail || (IMAGE_EXTENSIONS.has(extension.toLowerCase()) ? file : null);
      const showsPreview = Boolean(previewSource);

      if (showsPreview) {
        // Un aperçu qui ne charge pas laisserait l'icône d'image cassée du navigateur, pire que
        // pas d'aperçu du tout : on retombe sur la pastille de format.
        preview.addEventListener(
          'error',
          () => {
            preview.classList.add('hidden');
            mark.classList.remove('hidden');
          },
          { once: true },
        );
        preview.src = previewSource;
      }
      preview.classList.toggle('hidden', !showsPreview);
      mark.classList.toggle('hidden', showsPreview);
      mark.textContent = extension;

      const download = find('download');
      download.href = file ?? '#';
      download.hidden = !file;
      download.target = '_blank';
      download.rel = 'noopener';

      fragment.append(node);
    });

    if (append) {
      this.$refs.results.append(fragment);
    } else {
      this.$refs.results.replaceChildren(fragment);
    }

    this.$refs.empty.classList.toggle('hidden', data.totalHits > 0);
  }

  /**
   * Les étiquettes d'un hit, dans l'ordre où les familles sont déclarées.
   * @param {object} hit Un résultat de la réponse.
   * @returns {HTMLElement[]}
   */
  badgesFor(hit) {
    return this.badgeKeys
      .flatMap((key) => hit.meta?.[key] ?? [])
      .slice(0, MAX_BADGES)
      .map((label) => {
        const badge = this.$refs.badgeTemplate.content.firstElementChild.cloneNode(true);
        badge.textContent = label;

        return badge;
      });
  }

  /**
   * Show the panel the answer describes: fill each value's count, and hide what it does not carry
   * — a family left with nothing disappears whole, header included.
   * @param {object} data The endpoint's answer.
   * @returns {void}
   */
  renderCounts(data) {
    const flatten = (list) => Object.fromEntries((list ?? []).map((f) => [f.value, f.count]));
    const distributions = Object.fromEntries(
      Object.entries(data.facets?.meta ?? {}).map(([key, list]) => [key, flatten(list)]),
    );
    const facetKeyOf = Object.fromEntries(
      this.$refs.facetGroup.map((element) => [element.dataset.group, element.dataset.facetKey]),
    );

    this.$refs.facetValue.forEach((element, index) => {
      const { group, value } = element.dataset;
      const count = distributions[facetKeyOf[group]]?.[value] ?? 0;
      const input = this.$refs.facetInput[index];

      this.$refs.facetCount[index].textContent = `(${count})`;

      const isVisible = count > 0 || input.checked;
      element.classList.toggle('hidden', !isVisible);
      input.disabled = !isVisible;
    });

    this.$refs.facetGroup.forEach((element) => {
      const values = this.$refs.facetValue.filter(
        (value) => value.dataset.group === element.dataset.group,
      );

      element.classList.toggle(
        'hidden',
        values.every((value) => value.classList.contains('hidden')),
      );
    });
  }

  /**
   * One chip per checked value, each un-checking its own input.
   * @returns {void}
   */
  renderChips() {
    const fragment = document.createDocumentFragment();

    this.$refs.facetInput.forEach((input) => {
      if (!input.checked) {
        return;
      }

      const node = this.$refs.chipTemplate.content.cloneNode(true);
      const button = node.querySelector('button');
      // La puce reprend le libellé lisible que la locale a donné à la valeur, quand il y en a un.
      node.querySelector('[data-chip="label"]').textContent = input.dataset.label ?? input.value;

      button.addEventListener('click', () => {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      fragment.append(node);
    });

    this.$refs.chips.replaceChildren(fragment);
  }

  /**
   * « Effacer » ne s'affiche que s'il y a quelque chose à effacer : une facette cochée, ou un
   * terme de recherche. Se lit après `renderChips`, qui vient de reconstruire les puces.
   * @returns {void}
   */
  renderClear() {
    const clearable = this.$refs.chips.childElementCount > 0 || this.$refs.query.value !== '';

    this.$refs.filtersClear.forEach((button) => {
      button.toggleAttribute('data-clearable', clearable);
    });
  }

  /**
   * How many documents the whole search matches — the total, not what is on screen, which the
   * load-more block says just below. The sentence and its plural come from Liquid.
   * @param {object} data The endpoint's answer.
   * @returns {void}
   */
  renderSummary(data) {
    const { templateZero, templateOne, templateOther } = this.$refs.summary.dataset;
    // Zéro a sa propre forme : le français le veut au singulier, les autres langues au pluriel.
    let template = templateOther;

    if (data.totalHits === 0) {
      template = templateZero;
    } else if (data.totalHits === 1) {
      template = templateOne;
    }

    this.$refs.summary.textContent = template.replace('__COUNT__', data.totalHits);
  }

  /**
   * How many documents the visitor has picked. Empty at zero, so the separator disappears with it.
   * @returns {void}
   */
  renderSelection() {
    const { size } = this.selection;
    const { templateOne, templateOther } = this.$refs.selected.dataset;

    this.$refs.selected.textContent =
      size === 0 ? '' : (size === 1 ? templateOne : templateOther).replace('__COUNT__', size);
    this.$refs.selected.setAttribute('aria-pressed', String(this.showingSelection));
    this.$refs.selectAll.disabled = this.totalHits === 0;
    this.$refs.clearSelection.disabled = size === 0;
    this.$refs.downloadSelection.disabled = size === 0;
    // Séparé de `disabled`, que `setDownloading` lève aussi : le bouton porte le spinner
    // de l'archive, il doit rester à l'écran pendant la construction.
    this.$refs.downloadSelection.toggleAttribute('data-empty', size === 0);
  }

  /**
   * What a hit contributes to the selection, or `null` when it carries no file.
   * @param {object} hit One hit from the answer.
   * @returns {{ id: string, url: string, name: string }|null}
   */
  static fileOf(hit) {
    const url = hit.fields?.['custom.fichier'];

    if (!url) {
      return null;
    }

    const extension = hit.fields?.['custom.extension'];

    return { id: hit.id, url, name: extension ? `${hit.title}.${extension}` : hit.title };
  }

  /**
   * Show the selection instead of the results, or go back to the search.
   * @returns {void}
   */
  onSelectedClick() {
    this.showingSelection = !this.showingSelection;

    if (this.showingSelection) {
      this.renderSelectionView();
    } else {
      this.search();
    }
  }

  /**
   * Draw the picked documents, and only them.
   * @returns {void}
   */
  renderSelectionView() {
    if (this.selection.size === 0) {
      this.showingSelection = false;
      this.search();

      return;
    }

    const hits = [...this.selection.values()].map((entry) => entry.hit).filter(Boolean);

    this.renderHits({ hits, totalHits: hits.length }, false);
    this.$refs.loadMore.classList.add('hidden');
    this.$refs.summary.classList.add('hidden');
    this.renderSelection();
  }

  /**
   * Pick every document the CURRENT FILTERS match, not merely those on screen.
   * @returns {Promise<void>}
   */
  async onSelectAllClick() {
    this.setSelectingAll(true);

    try {
      const params = new URLSearchParams(this.params);
      params.set('limit', String(MAX_SEARCH_LIMIT));

      let page = 1;
      let reachable = MAX_SEARCH_LIMIT;
      // Mis de côté plutôt qu'ajouté au fil de l'eau : une nouvelle recherche abandonne la
      // requête en cours, et les pages déjà parcourues laisseraient une sélection partielle que
      // le visiteur n'a pas demandée. Les documents cochés avant le parcours, eux, restent.
      const picked = new Map();

      while ((page - 1) * MAX_SEARCH_LIMIT < reachable) {
        params.set('page', String(page));

        const data = await this.fetchSearch(params);

        reachable = Math.min(data.totalHits, MAX_TOTAL_HITS);
        data.hits.forEach((hit) => {
          const entry = DocumentationSearch.fileOf(hit);

          if (entry) {
            picked.set(entry.id, { url: entry.url, name: entry.name, hit });
          }
        });
        page += 1;
      }

      picked.forEach((entry, id) => this.selection.set(id, entry));

      if (this.showingSelection) {
        this.renderSelectionView();

        return;
      }

      this.$refs.results
        .querySelectorAll('[data-hit="select"]')
        .forEach((input) => (input.checked = this.selection.has(input.value)));
      this.renderSelection();
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error(error);
      this.$refs.error.classList.remove('hidden');
    } finally {
      this.setSelectingAll(false);
    }
  }

  /**
   * @param {boolean} isSelecting Whether the walk through the results is running.
   * @returns {void}
   */
  setSelectingAll(isSelecting) {
    this.$refs.selectAll.disabled = isSelecting || this.totalHits === 0;
    this.$refs.selectAll.setAttribute('aria-busy', String(isSelecting));
    this.$refs.selectAllSpinner.classList.toggle('hidden', !isSelecting);
  }

  /**
   * Drop every pick, and come back to the results if the selection was what was on screen.
   * @returns {void}
   */
  onClearSelectionClick() {
    this.selection.clear();
    this.$refs.results
      .querySelectorAll('[data-hit="select"]')
      .forEach((input) => (input.checked = false));

    if (this.showingSelection) {
      this.search();

      return;
    }

    this.renderSelection();
  }

  /**
   * Fetch one document from the CDN.
   * @param {string} url Where the file lives.
   * @returns {Promise<Blob>} The file.
   */
  static async fetchFile(url) {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`The CDN answered ${response.status} for ${url}.`);
    }

    return response.blob();
  }

  /**
   * Hand a blob to the browser as a download.
   * @param {Blob} blob What to save.
   * @param {string} name The name to save it under.
   * @returns {void}
   */
  static save(blob, name) {
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = href;
    link.download = name;
    link.click();
    URL.revokeObjectURL(href);
  }

  /**
   * Hand the picked documents to the browser: the file itself when only one is picked, an
   * archive otherwise.
   * @returns {Promise<void>}
   */
  async onDownloadSelectionClick() {
    this.setDownloading(true);
    // Le téléchargement se construit fichier par fichier, sans repère de progression : le toast
    // dit au moins que le clic a été pris.
    this.$root.Toaster.addToast(this.$refs.downloadSelection.dataset.started);

    try {
      const picked = [...this.selection.values()];

      if (picked.length === 1) {
        const [{ url, name }] = picked;

        DocumentationSearch.save(await DocumentationSearch.fetchFile(url), name);

        return;
      }

      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      const used = new Set();

      for (const { url, name } of picked) {
        let entry = name;
        for (let index = 2; used.has(entry); index += 1) {
          entry = name.replace(/(\.[^.]+)?$/, `-${index}$1`);
        }
        used.add(entry);

        zip.file(entry, await DocumentationSearch.fetchFile(url));
      }

      const archive = await zip.generateAsync({ type: 'blob', compression: 'STORE' });

      DocumentationSearch.save(archive, `${this.$refs.downloadSelection.dataset.filename}.zip`);
    } catch (error) {
      console.error(error);
      this.$refs.error.classList.remove('hidden');
    } finally {
      this.setDownloading(false);
    }
  }

  /**
   * @param {boolean} isDownloading Whether the archive is being built.
   * @returns {void}
   */
  setDownloading(isDownloading) {
    this.$refs.downloadSelection.disabled = isDownloading || this.selection.size === 0;
    this.$refs.downloadSelection.setAttribute('aria-busy', String(isDownloading));
    this.$refs.downloadSpinner.classList.toggle('hidden', !isDownloading);
  }

  /**
   * The load-more block: how many hits are on screen, how far that is, and whether there is more.
   * @param {object} data The endpoint's answer.
   * @returns {void}
   */
  renderLoadMore(data) {
    const loaded = this.$refs.results.childElementCount;
    const reachable = Math.min(data.totalHits, MAX_TOTAL_HITS);

    this.$refs.loaded.textContent = this.$refs.loaded.dataset.template
      .replace('__COUNT__', loaded)
      .replace('__TOTAL__', data.totalHits);
    this.$refs.progress.style.width = `${reachable === 0 ? 0 : (loaded / reachable) * 100}%`;
    this.$refs.loadMore.classList.toggle('hidden', loaded >= reachable);
  }
}
